import { resolveTitle } from "./sourceFields";

/**
 * Whether the Digital Twin shows the vehicle identifier (licence plate) or the
 * device ID, and the labels for each. Driven by globalConfig.baseEntity, with the
 * old batteryConfig.application === "bus" rule kept for orgs that have not set it.
 */

const isBlank = (value) =>
    value === undefined || value === null || String(value).trim() === "";

const baseEntityType = (globalConfig) => {
    const type = globalConfig?.baseEntity?.type;
    return isBlank(type) ? null : String(type).trim().toLowerCase();
};

/**
 * True when the Digital Twin should identify the asset by its licence plate.
 *  - globalConfig.baseEntity.type set: only for "vehicle", and only when a plate
 *    exists (otherwise the device ID is shown, as today).
 *  - not set: the legacy rule, batteryConfig.application === "bus".
 */
export const shouldShowVehicleIdentifier = ({ globalConfig, batteryConfig, licensePlateNo } = {}) => {
    const type = baseEntityType(globalConfig);
    if (type) return type === "vehicle" && !isBlank(licensePlateNo);
    return String(batteryConfig?.application || "").toLowerCase() === "bus";
};

/** Label for the licence plate: baseEntity.idLabel, else the caller's fallback. */
export const resolveVehicleIdLabel = (globalConfig, fallbackLabel) =>
    globalConfig?.baseEntity?.idLabel || fallbackLabel;

/**
 * Label for the device ID. A battery-type org's baseEntity.idLabel names the
 * device ID (e.g. "IMEI"), so it is used; for a vehicle org idLabel names the
 * plate, so the device ID keeps the caller's fallback.
 */
export const resolveDeviceIdLabel = (globalConfig, fallbackLabel) => {
    if (baseEntityType(globalConfig) === "vehicle") return fallbackLabel;
    return globalConfig?.baseEntity?.idLabel || fallbackLabel;
};

/**
 * Label for the licence plate where it is shown whatever the org type (the Vitals vehicle
 * node). The mirror of resolveDeviceIdLabel: a battery-type org's idLabel names the device
 * ID (e.g. "IMEI"), so it must not label the plate; such an org gets otherOrgLabel.
 */
export const resolvePlateLabel = (globalConfig, vehicleOrgLabel, otherOrgLabel) => {
    const type = baseEntityType(globalConfig);
    if (type && type !== "vehicle") return otherOrgLabel;
    return globalConfig?.baseEntity?.idLabel || vehicleOrgLabel;
};

/**
 * The org's ID box config for one hierarchy level of a Vitals tab, from the digitalTwin
 * page meta: metaInfo.<section>.idDisplay.<level>, where section is "batteryVitals" or
 * "vehicleVitals". Same shape as the header title: { label, key, source, fallback }, with
 * source "batteryInfo" (default), "vehicleInfo" or "node" (the selected hierarchy node:
 * its info plus displayId). null when the org has not configured the level, or the entry
 * has no key or label.
 *
 * The vehicle node also appears on Battery Vitals; without its own batteryVitals entry it
 * uses vehicleVitals.idDisplay.vehicle, so one vehicle reads the same on both tabs.
 */
export const getVitalsIdDisplayConfig = (pageContent, section, level) => {
    const normalizedLevel = String(level || "").toLowerCase();
    const entryFor = (sectionName) => {
        const entry = pageContent?.metaInfo?.[sectionName]?.idDisplay?.[normalizedLevel];
        return entry && typeof entry === "object" && !isBlank(entry.key) && !isBlank(entry.label) ? entry : null;
    };
    const entry = entryFor(section);
    if (entry || normalizedLevel !== "vehicle" || section === "vehicleVitals") return entry;
    return entryFor("vehicleVitals");
};

/** { label, value } from an idDisplay entry; value is null when it and its fallback are empty. */
export const resolveConfiguredIdDisplay = (config, { batteryInfo, vehicleInfo, node } = {}) =>
    resolveTitle(config, {
        batteryInfo,
        vehicleInfo,
        node: node ? { ...node.info, displayId: node.displayId } : undefined,
    });

/**
 * { label, value } for the Battery Vitals selected-node ID box.
 *  - vehicle: "<idLabel | Bus Number>: <plate>"
 *  - battery / tms with the vehicle identifier on: "<idLabel | Bus Number>: <plate>"
 *  - battery otherwise: "<Battery> ID: <device ID>"; tms otherwise: "<TMS> ID: <plate>"
 *  - every other level: "<Level> ID: <displayId>"
 * value is null when there is nothing to show.
 * An org's idDisplayConfig (getVitalsIdDisplayConfig) replaces all of the above for its level.
 *
 * @param {object} args
 * @param {object} args.node                 - selected hierarchy node ({ level, displayId })
 * @param {boolean} args.showVehicleIdentifier
 * @param {string} args.vehicleIdLabel       - resolved plate label
 * @param {string} args.levelLabel           - translated level name (e.g. "Battery")
 * @param {string} [args.licensePlateNo]
 * @param {string} [args.deviceID]
 * @param {object} [args.idDisplayConfig]   - the level's org config, or null
 * @param {object} [args.batteryInfo]       - sources for idDisplayConfig
 * @param {object} [args.vehicleInfo]
 */
export const resolveVitalsNodeIdDisplay = ({
    node,
    showVehicleIdentifier,
    vehicleIdLabel,
    levelLabel,
    licensePlateNo,
    deviceID,
    idDisplayConfig = null,
    batteryInfo,
    vehicleInfo,
}) => {
    if (idDisplayConfig) {
        return resolveConfiguredIdDisplay(idDisplayConfig, { batteryInfo, vehicleInfo, node });
    }
    const level = node?.level;
    const plate = isBlank(licensePlateNo) ? null : licensePlateNo;
    if (level === "vehicle" || (showVehicleIdentifier && (level === "battery" || level === "tms"))) {
        return { label: vehicleIdLabel, value: plate };
    }
    const label = `${levelLabel} ID`;
    if (level === "battery") return { label, value: isBlank(deviceID) ? null : deviceID };
    if (level === "tms") return { label, value: plate };
    return { label, value: isBlank(node?.displayId) ? null : node.displayId };
};

/**
 * { label, value } naming the asset as the Digital Twin header does: the licence plate
 * when the vehicle identifier is on, else the device ID (IMEI). value is null when the
 * chosen field is blank. Used for the identifier column of the Device Health download.
 *
 * @param {object} args
 * @param {boolean} args.showVehicleIdentifier - shouldShowVehicleIdentifier(...)
 * @param {string} args.vehicleIdLabel         - resolved plate label
 * @param {string} args.deviceIdLabel          - resolveDeviceIdLabel(...)
 * @param {string} [args.licensePlateNo]
 * @param {string} [args.deviceID]
 */
export const resolvePrimaryIdentifier = ({
    showVehicleIdentifier,
    vehicleIdLabel,
    deviceIdLabel,
    licensePlateNo,
    deviceID,
}) => {
    if (showVehicleIdentifier) {
        return { label: vehicleIdLabel, value: isBlank(licensePlateNo) ? null : licensePlateNo };
    }
    return { label: deviceIdLabel, value: isBlank(deviceID) ? null : deviceID };
};

/**
 * { label, value } for the Battery Vitals battery-level boxes (the instantaneous table and
 * the comparison dialog): the org's batteryVitals.idDisplay.battery when set, else the
 * plate or device ID exactly as resolvePrimaryIdentifier picks them. value is null when
 * there is nothing to show, and the caller then hides the box.
 *
 * @param {object} args
 * @param {object} [args.idDisplayConfig]      - getVitalsIdDisplayConfig(..., "battery"), or null
 * @param {object} [args.batteryInfo]
 * @param {object} [args.vehicleInfo]
 * @param {boolean} args.showVehicleIdentifier
 * @param {string} args.vehicleIdLabel
 * @param {string} args.deviceIdLabel
 */
export const resolveBatteryIdDisplay = ({
    idDisplayConfig = null,
    batteryInfo,
    vehicleInfo,
    showVehicleIdentifier,
    vehicleIdLabel,
    deviceIdLabel,
}) => {
    if (idDisplayConfig) {
        return resolveConfiguredIdDisplay(idDisplayConfig, { batteryInfo, vehicleInfo });
    }
    return resolvePrimaryIdentifier({
        showVehicleIdentifier,
        vehicleIdLabel,
        deviceIdLabel,
        licensePlateNo: vehicleInfo?.licensePlateNo,
        deviceID: batteryInfo?.deviceID,
    });
};
